export interface SerializedLayer {
  id: string;
  name: string;
  type: 'raster' | 'text';
  visible: boolean;
  locked: boolean;
  opacity: number;
  blendMode: any;
  x: number;
  y: number;
  width: number;
  height: number;
  dataUri: string;
  textData?: any;
}

export interface SerializedProject {
  version: string;
  name: string;
  width: number;
  height: number;
  timestamp: number;
  isBackgroundSizeLocked: boolean;
  layers: SerializedLayer[];
}

const DB_NAME = 'IronCropStorageDB';
const DB_VERSION = 1;
const STORE_NAME = 'projects';
const KEY_ACTIVE = 'active_project';
const LS_FALLBACK_KEY = 'ironcrop_active_project';

export class ProjectStorage {
  private static dbPromise: Promise<IDBDatabase> | null = null;

  private static getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        return reject(new Error('IndexedDB not supported'));
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e: any) => {
        const db = e.target.result as IDBDatabase;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    return this.dbPromise;
  }

  /**
   * Save active project to IndexedDB (with localStorage backup for small projects)
   */
  public static async save(project: SerializedProject): Promise<boolean> {
    try {
      const db = await this.getDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(project, KEY_ACTIVE);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
      return true;
    } catch (err) {
      // Fallback to localStorage if IndexedDB failed
      try {
        const json = JSON.stringify(project);
        localStorage.setItem(LS_FALLBACK_KEY, json);
        return true;
      } catch (lsErr) {
        console.warn('Could not save to localStorage quota:', lsErr);
        return false;
      }
    }
  }

  /**
   * Load active project from IndexedDB (or localStorage fallback)
   */
  public static async load(): Promise<SerializedProject | null> {
    try {
      const db = await this.getDB();
      const proj = await new Promise<SerializedProject | null>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(KEY_ACTIVE);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
      });

      if (proj && proj.layers && proj.width && proj.height) {
        return proj;
      }
    } catch (err) {
      console.warn('IndexedDB load error, checking localStorage:', err);
    }

    // Try localStorage fallback
    try {
      const raw = localStorage.getItem(LS_FALLBACK_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.layers && parsed.width && parsed.height) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignore
    }

    return null;
  }

  /**
   * Check if a saved project exists
   */
  public static async hasSaved(): Promise<boolean> {
    try {
      const project = await this.load();
      return project !== null && project.layers.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Delete saved project from both IndexedDB and localStorage
   */
  public static async clear(): Promise<void> {
    try {
      const db = await this.getDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(KEY_ACTIVE);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.warn('IndexedDB clear error:', err);
    }

    try {
      localStorage.removeItem(LS_FALLBACK_KEY);
    } catch {
      // Ignore
    }
  }
}
